<template>
    <Dialog v-model="showModal" :disabled="isLoading" class="sm:max-w-[540px]">
        <DialogHeader>
            <DialogTitle>{{ $t('views.monitoring.teamOverview.modals.editTeam.title') }}</DialogTitle>
            <VisuallyHidden>
                <DialogDescription class="pt-4">{{ $t('views.monitoring.teamOverview.modals.editTeam.description') }}</DialogDescription>
            </VisuallyHidden>
        </DialogHeader>
        <form class="mt-4 space-y-2" @submit.prevent="handleEdit">
            <CustomInput :value="(editState?.locationNameAugmented as any) ?? ''" disabled />
            <CustomInput
                v-model:value="(editState.name as any)"
                :rules="[{ validate: (value: string) => value.length > 0, message: '' }]"
                required
                :label="$t('views.monitoring.teamOverview.modals.editTeam.fields.name')"
            />
            <div v-if="state.barn.animal_group_id === 3" class="pb-4">
                <RadioGroup v-model="editState.animalGroupId" class="gap-x-5">
                    <div v-for="option in animalGroupOptions" :key="option.value" class="flex items-center cursor-pointer">
                        <RadioGroupItem :id="`option-id-${option.value}`" :value="option.value.toString()" />
                        <Label :for="`option-id-${option.value}`" class="font-normal cursor-pointer pl-2">{{ option.label }}</Label>
                    </div>
                </RadioGroup>
            </div>
            <div class="grid grid-cols-2 gap-x-4" tabindex="-1">
                <DatePicker v-model:value="editState.startDate" required :label="$t('views.monitoring.teamOverview.modals.editTeam.fields.startDate')" />
                <DatePicker v-model:value="editState.endDate" resetable :label="$t('views.monitoring.teamOverview.modals.editTeam.fields.endDate')" />
            </div>
            <CustomCheckbox v-model:checked="editState.isFavorite" :label="$t('views.monitoring.teamOverview.modals.editTeam.fields.markAsFavorite')" class="-ml-1" />
        </form>
        <CustomButton :disabled="isLoading" color-preset="warning" @click.prevent="emit('delete', props.state.team.id)">
            {{ $t('views.monitoring.teamOverview.modals.editTeam.buttons.deleteTeam') }}
        </CustomButton>
        <DialogFooter>
            <DialogClose as-child>
                <CustomButton :disabled="isLoading" color-preset="white">{{ $t('views.monitoring.teamOverview.modals.editTeam.buttons.cancel') }}</CustomButton>
            </DialogClose>
            <CustomButton :loading="isLoading" :disabled="!isFormValid" @click="handleEdit">{{ $t('views.monitoring.teamOverview.modals.editTeam.buttons.save') }}</CustomButton>
        </DialogFooter>
    </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useQueryClient } from '@tanstack/vue-query';
import { VisuallyHidden } from 'radix-vue';
import { useToast } from 'vue-toastification';
import { addWeeks, format, parseISO } from 'date-fns';
import CustomButton from '@/components/ui/CustomButton.vue';
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { queryKeys } from '@/plugins/store/actions/queries/study-periods';
import DatePicker from '@/components/ui/DatePicker.vue';
import CustomInput from '@/components/ui/CustomInput.vue';
import CustomCheckbox from '@/components/ui/CustomCheckbox.vue';
import ApiClient from '@/plugins/store/actions/api/ApiClient';
import { Barn, StudyPeriod } from '@/types';
import { i18n } from '@/plugins/internationalization/i18n';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';

const props = defineProps<{
    state: { barn: Barn; team: StudyPeriod };
}>();
const emit = defineEmits<{
    delete: [teamId: number];
}>();

const queryClient = useQueryClient();
const toast = useToast();

const showModal = defineModel<boolean>('modelValue');

const isLoading = ref(false);

interface EditTeamState {
    locationNameAugmented: string;
    name: string;
    startDate: string;
    endDate: string;
    isFavorite: boolean;
    animalGroupId: string;
}

function getInitialState() {
    return {
        locationNameAugmented: '',
        name: '',
        startDate: new Date().toISOString(),
        endDate: addWeeks(new Date(), 1).toISOString(),
        isFavorite: false,
        animalGroupId: '1',
    };
}

const animalGroupOptions = computed(() => [
    {
        value: 1,
        label: i18n.global.t('views.monitoring.teamOverview.modals.editTeam.fields.animalGroup.options.weaners'),
    },
    {
        value: 2,
        label: i18n.global.t('views.monitoring.teamOverview.modals.editTeam.fields.animalGroup.options.slaughterPigs'),
    },
    {
        value: 3,
        label: i18n.global.t('views.monitoring.teamOverview.modals.editTeam.fields.animalGroup.options.frats'),
    },
]);

const editState = ref<EditTeamState>(getInitialState());

async function handleEdit() {
    try {
        if (isLoading.value) {
            return;
        }

        isLoading.value = true;

        await ApiClient.send(
            'put',
            'monitor/study-periods/bulk-update',
            {
                skipHandleError: true,
            },
            {
                study_periods: [
                    {
                        id: props.state.team.id,
                        name: editState.value.name,
                        start: format(parseISO(editState.value.startDate), 'yyyy-MM-dd'),
                        end: editState.value.endDate ? format(parseISO(editState.value.endDate), 'yyyy-MM-dd') : null,
                        is_favorite: editState.value.isFavorite,
                        animal_group_id: Number(editState.value.animalGroupId),
                    },
                ],
            }
        );

        await queryClient.invalidateQueries({ queryKey: queryKeys.getStudyPeriodsRoot });

        showModal.value = false;

        toast.success(i18n.global.t('views.monitoring.teamOverview.modals.editTeam.successMessages.teamWasEdited'));
    } catch (err) {
        const errorMessage = (err as any)?.data?.message || i18n.global.t('views.monitoring.teamOverview.modals.editTeam.errors.couldNotEditTeam');
        toast.error(errorMessage);
    } finally {
        isLoading.value = false;
    }
}

const isFormValid = computed(() => editState.value.name.length > 0);

watch(showModal, (value) => {
    if (value) {
        editState.value = {
            locationNameAugmented: props.state.team.location_name_augmented ?? '',
            name: props.state.team.name ?? '',
            startDate: props.state.team.start ? new Date(props.state.team.start as string).toISOString() : new Date().toISOString(),
            endDate: props.state.team.end ? new Date(props.state.team.end as string).toISOString() : '',
            isFavorite: props.state.team.is_favorite ?? false,
            animalGroupId: props.state.barn.animal_group_id?.toString() ?? '1',
        };
    }
});
</script>
